import { useState } from "react";
import { Link } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { RotateCcw } from "lucide-react";
import { Disclaimer } from "./shared";

const QUESTIONS = [
  { q: "What is your age group?", o: [["Above 60", 1], ["46 – 60", 2], ["31 – 45", 3], ["Below 30", 4]] },
  { q: "What is your investment horizon for most of your money?", o: [["Less than 1 year", 1], ["1 – 3 years", 2], ["3 – 7 years", 3], ["More than 7 years", 4]] },
  { q: "How stable is your income?", o: [["Uncertain / irregular", 1], ["Somewhat stable", 2], ["Stable", 3], ["Very stable with growth", 4]] },
  { q: "Do you have an emergency fund of 6 months' expenses?", o: [["No", 1], ["Less than 3 months", 2], ["3 – 6 months", 3], ["More than 6 months", 4]] },
  { q: "If your portfolio fell 20% in a month, you would…", o: [["Sell everything", 1], ["Sell some", 2], ["Hold and wait", 3], ["Invest more", 4]] },
  { q: "What is your primary investment objective?", o: [["Protect capital", 1], ["Regular income", 2], ["Balanced growth", 3], ["Maximum long-term growth", 4]] },
  { q: "How familiar are you with equity and mutual funds?", o: [["Not familiar", 1], ["Basic understanding", 2], ["Fairly experienced", 3], ["Very experienced", 4]] },
];

const PROFILES = [
  { max: 11, name: "Conservative", text: "You prioritise capital protection and stability over growth.", mix: [15, 70, 10, 5] },
  { max: 15, name: "Moderately Conservative", text: "You seek modest growth while keeping volatility low.", mix: [30, 55, 10, 5] },
  { max: 19, name: "Moderate", text: "You are comfortable balancing growth and stability.", mix: [50, 38, 8, 4] },
  { max: 23, name: "Moderately Aggressive", text: "You can accept meaningful volatility for higher long-term growth.", mix: [65, 25, 7, 3] },
  { max: 28, name: "Aggressive", text: "You have a long horizon and high tolerance for market swings.", mix: [80, 12, 5, 3] },
];
const ASSETS = ["Equity", "Debt", "Gold", "Cash"];
const COLORS = ["#00C987", "#071426", "#D6B36A", "#94A3B8"];

function Result({ score, onReset }) {
  const p = PROFILES.find((x) => score <= x.max);
  const data = ASSETS.map((name, i) => ({ name, value: p.mix[i] }));
  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="risk-result">
      <div className="rounded-3xl bg-navy p-8 text-white">
        <p className="text-xs uppercase tracking-[0.2em] text-gold">Your indicative risk profile</p>
        <h3 className="mt-3 text-3xl font-bold" data-testid="risk-result-profile">{p.name}</h3>
        <p className="font-num mt-2 text-sm text-slate-400">Score {score} / 28</p>
        <p className="mt-4 text-slate-300">{p.text}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/book-consultation" data-testid="risk-book-btn" className="rounded-full bg-mint px-5 py-3 text-sm font-semibold text-navy">Discuss in a free consultation</Link>
          <button onClick={onReset} data-testid="risk-retake-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm"><RotateCcw size={14} /> Retake</button>
        </div>
      </div>
      <div className="rounded-3xl border border-slate-200 bg-white p-8">
        <p className="text-sm font-semibold text-navy">Illustrative asset-allocation range</p>
        <div className="h-56"><ResponsiveContainer><PieChart><Pie data={data} dataKey="value" innerRadius={55} outerRadius={90} paddingAngle={2}>{data.map((d, i) => <Cell key={d.name} fill={COLORS[i]} />)}</Pie><Tooltip formatter={(v) => `${v}%`} /></PieChart></ResponsiveContainer></div>
        <div className="grid grid-cols-2 gap-2 text-sm">{data.map((d, i) => <span key={d.name} className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: COLORS[i] }} />{d.name} <b className="font-num">{d.value}%</b></span>)}</div>
        <div className="mt-6"><Disclaimer>This assessment is educational and indicative only. It is not a suitability assessment or investment advice. Allocations shown are illustrative and should be discussed with an appropriately registered adviser.</Disclaimer></div>
      </div>
    </div>
  );
}

export default function RiskProfile() {
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const total = Object.values(answers).reduce((a, b) => a + b, 0);
  const complete = Object.keys(answers).length === QUESTIONS.length;
  if (done) return <Result score={total} onReset={() => { setAnswers({}); setDone(false); }} />;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="risk-quiz">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-navy">Risk Profile Assessment</h3>
        <span className="font-num text-sm text-slate-500" data-testid="risk-progress">{Object.keys(answers).length}/{QUESTIONS.length}</span>
      </div>
      <div className="mt-8 space-y-8">
        {QUESTIONS.map((q, qi) => (
          <div key={q.q}>
            <p className="font-semibold text-navy">{qi + 1}. {q.q}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {q.o.map(([label, score], oi) => (
                <button key={label} type="button" onClick={() => setAnswers({ ...answers, [qi]: score })} data-testid={`risk-q${qi}-opt${oi}`}
                  className={`rounded-xl border px-4 py-3 text-left text-sm transition-colors ${answers[qi] === score ? "border-mint bg-mint/10 font-semibold text-navy" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button disabled={!complete} onClick={() => setDone(true)} data-testid="risk-submit-btn"
        className="mt-10 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-opacity disabled:opacity-40">See my risk profile</button>
    </div>
  );
}
